import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { PrimaryButton } from '../components/PrimaryButton';
import { CancelPlanModal } from '../components/CancelPlanModal';
import {
  cancelMyMembership,
  customerApiErrorMessage,
  getMyMembership,
  listMembershipPlans,
  startMembershipCheckout,
  type MembershipInfo,
  type MembershipPlanOption,
} from '../../lib/customer-api';
import { RequireCustomer } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '';
  }
};

const buildPriceLine = (m: MembershipInfo): { amount: string; per: string } => {
  const plan = m.subscription;
  if (!plan) {
    return {
      amount: formatMoney(m.trans_amount, m.currency),
      per: m.subscription_type === 'YEARLY' ? '/year' : '/month',
    };
  }
  const cycle = m.subscription_type === 'YEARLY' ? 'yearly_price' : 'monthly_price';
  const value = Number(plan[cycle] ?? m.trans_amount);
  return {
    amount: formatMoney(value, plan.currency ?? m.currency),
    per: m.subscription_type === 'YEARLY' ? '/year' : '/month',
  };
};

export default function BillingSubscriptionPage() {
  return (
    <RequireCustomer>
      <BillingSubscriptionPageInner />
    </RequireCustomer>
  );
}

function BillingSubscriptionPageInner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [membership, setMembership] = useState<MembershipInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Plan picker (shown when there's no active membership).
  const [plans, setPlans] = useState<MembershipPlanOption[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'YEARLY'>(
    'MONTHLY',
  );
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMyMembership();
      setMembership(res.info ?? null);
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not load your membership.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await listMembershipPlans();
      // Backend filters by is_active server-side; defensive check
      // here keeps a misconfigured admin row out of the picker.
      setPlans((res.list ?? []).filter((p) => p.is_active !== false));
    } catch {
      // Picker is non-critical — leave empty rather than blocking
      // the page on a transient catalogue error.
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadPlans();
  }, []);

  // Stripe Checkout returns the customer here with ?status=success/cancelled.
  // We surface a toast + clean the URL so a refresh doesn't re-trigger
  // the message. Webhook handles the actual status flip; a short reload
  // usually shows ACTIVE on the next fetch.
  useEffect(() => {
    const status = searchParams.get('status');
    if (!status) return;
    if (status === 'success') {
      toast.success(
        'Payment received — your membership will be active in a moment.',
      );
      load();
    } else if (status === 'cancelled') {
      toast.error('Checkout cancelled — no charge was made.');
    }
    // Strip the query params so a refresh doesn't replay the toast.
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('session');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancelPlan = async () => {
    setCancelling(true);
    try {
      await cancelMyMembership();
      toast.success('Your plan will not renew at the end of the period.');
      setIsCancelModalOpen(false);
      await load();
    } catch (err) {
      toast.error(customerApiErrorMessage(err, 'Could not cancel your plan.'));
    } finally {
      setCancelling(false);
    }
  };

  const handleSubscribe = async (plan: MembershipPlanOption) => {
    setCheckoutPlanId(plan.id);
    try {
      const res = await startMembershipCheckout({
        subscription_id: plan.id,
        subscription_type: billingCycle,
        auto_renew: true,
      });
      if (res.checkout_url) {
        // Redirect to Stripe Checkout. Customer returns via successUrl
        // (which we now point at this page with ?status=success).
        window.location.assign(res.checkout_url);
      } else {
        toast.error('Could not start checkout. Please try again.');
        setCheckoutPlanId(null);
      }
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not start checkout.'),
      );
      setCheckoutPlanId(null);
    }
  };

  const planName = membership?.subscription?.name ?? 'Membership';
  const planTagline = membership?.subscription?.tagline ?? '';
  const features = membership?.subscription?.features ?? [];
  const renewsOn = formatDate(membership?.end_date ?? null);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-12">
        {/* Back and Title */}
        <div className="flex items-start gap-4 mb-1">
          <Link
            to="/account-settings"
            className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
          >
            <span className="material-icons text-[#908e8e] text-[18px]">
              chevron_left
            </span>
          </Link>
          <div>
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
              Billing &amp; Subscription
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Manage your Cherry Picked membership
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row justify-between gap-12">
          {/* Left Column */}
          <div className="flex-1 max-w-[553px]">
            {/* Active Subscription plan */}
            <div>
              <h2 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] mb-6">
                Active Subscription plan
              </h2>

              {loading ? (
                <div className="py-16 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
                  Loading your membership…
                </div>
              ) : !membership ? (
                <div className="space-y-6">
                  <div className="bg-white border border-[#f1f2f4] rounded-[16px] p-6">
                    <h3 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#111827] text-[18px] tracking-[-0.09px] mb-2">
                      No active membership
                    </h3>
                    <p className="font-['DM_Sans',sans-serif] text-[#687588] text-[14px] tracking-[-0.1px] mb-5">
                      Pick a Cherry Picked plan to unlock booking discounts,
                      priority slots and loyalty credit.
                    </p>

                    {/* Monthly / Yearly toggle */}
                    <div className="inline-flex p-1 rounded-[8px] bg-[#f1f2f4]">
                      {(['MONTHLY', 'YEARLY'] as const).map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setBillingCycle(c)}
                          className={`px-4 h-[36px] rounded-[6px] font-['DM_Sans',sans-serif] font-bold text-[13px] transition-all ${
                            billingCycle === c
                              ? 'bg-white text-[#3a3335] shadow-sm'
                              : 'text-[#687588] hover:text-[#3a3335]'
                          }`}
                        >
                          {c === 'MONTHLY' ? 'Monthly' : 'Yearly'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Plan grid */}
                  {plansLoading ? (
                    <div className="bg-white border border-[#f1f2f4] rounded-[16px] p-12 text-center text-[#687588] font-['DM_Sans',sans-serif] text-[14px]">
                      Loading plans…
                    </div>
                  ) : plans.length === 0 ? (
                    <div className="bg-white border border-[#f1f2f4] rounded-[16px] p-12 text-center">
                      <p className="font-['DM_Sans',sans-serif] text-[#687588] text-[14px]">
                        No plans are available right now. Please check back
                        later.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      {plans.map((plan) => {
                        const cyclePrice =
                          billingCycle === 'YEARLY'
                            ? Number(plan.yearly_price)
                            : Number(plan.monthly_price);
                        const planFeatures = plan.features ?? [];
                        const isCheckingOut = checkoutPlanId === plan.id;
                        return (
                          <div
                            key={plan.id}
                            className="bg-white border border-[#f1f2f4] rounded-[16px] p-6"
                          >
                            <div className="flex justify-between items-start gap-4 mb-4">
                              <div>
                                <h3 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#111827] text-[22px] tracking-[-0.09px] mb-1">
                                  {plan.name}
                                </h3>
                                {plan.tagline ? (
                                  <p className="font-['DM_Sans',sans-serif] text-[#687588] text-[14px] tracking-[-0.1px]">
                                    {plan.tagline}
                                  </p>
                                ) : null}
                              </div>
                              <div className="text-right shrink-0">
                                <div className="flex items-baseline gap-1 justify-end">
                                  <span className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[22px] tracking-[-0.09px]">
                                    {formatMoney(cyclePrice, plan.currency)}
                                  </span>
                                  <span className="font-['DM_Sans',sans-serif] text-[#687588] text-[12px] tracking-[-0.1px]">
                                    {billingCycle === 'YEARLY' ? '/yr' : '/mo'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {planFeatures.length > 0 ? (
                              <>
                                <div className="h-[1px] bg-[#f1f2f4] w-full mb-4" />
                                <ul className="space-y-2 mb-5">
                                  {planFeatures.map((feature, i) => (
                                    <li
                                      key={i}
                                      className="flex items-start gap-3"
                                    >
                                      <span className="material-icons text-[#8e2a2f] text-[18px] mt-[1px]">
                                        check
                                      </span>
                                      <span className="font-['DM_Sans',sans-serif] text-[#111827] text-[14px] leading-[20px] tracking-[-0.1px]">
                                        {feature}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            ) : null}

                            <PrimaryButton
                              className="w-full h-[44px] text-[14px]"
                              onClick={() => handleSubscribe(plan)}
                              disabled={
                                cyclePrice <= 0 || checkoutPlanId !== null
                              }
                            >
                              {isCheckingOut
                                ? 'Redirecting to checkout…'
                                : cyclePrice <= 0
                                  ? 'Not available'
                                  : 'Subscribe'}
                            </PrimaryButton>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-[#f1f2f4] rounded-[16px] p-6">
                  {/* Plan Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#111827] text-[22px] tracking-[-0.09px] mb-1">
                        {planName}
                      </h3>
                      {planTagline ? (
                        <p className="font-['DM_Sans',sans-serif] text-[#687588] text-[14px] tracking-[-0.1px]">
                          {planTagline}
                        </p>
                      ) : null}
                    </div>
                    {membership.auto_renew ? (
                      <PrimaryButton
                        className="w-auto px-6 h-[44px] text-[16px]"
                        onClick={() => setIsCancelModalOpen(true)}
                        disabled={cancelling}
                      >
                        {cancelling ? 'Cancelling…' : 'Cancel plan'}
                      </PrimaryButton>
                    ) : (
                      <span className="shrink-0 h-[31px] px-5 rounded-[30px] bg-[#3a3335]/[0.06] text-[#908e8e] font-['DM_Sans',sans-serif] font-bold text-[14px] flex items-center">
                        Cancellation scheduled
                      </span>
                    )}
                  </div>

                  <div className="h-[1px] bg-[#f1f2f4] w-full mb-6" />

                  {/* Plan Price */}
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[22px] tracking-[-0.09px]">
                      {buildPriceLine(membership).amount}
                    </span>
                    <span className="font-['DM_Sans',sans-serif] text-[#687588] text-[12px] tracking-[-0.1px]">
                      {buildPriceLine(membership).per}
                    </span>
                  </div>

                  {features.length > 0 ? (
                    <>
                      <div className="h-[1px] bg-[#f1f2f4] w-full mb-6" />
                      <div className="space-y-3 mb-6">
                        {features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-[18px] h-[18px] flex items-center justify-center shrink-0">
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M15.5303 4.71967C15.8232 5.01256 15.8232 5.48744 15.5303 5.78033L8.03033 13.2803C7.73744 13.5732 7.26256 13.5732 6.96967 13.2803L3.21967 9.53033C2.92678 9.23744 2.92678 8.76256 3.21967 8.46967C3.51256 8.17678 3.98744 8.17678 4.28033 8.46967L7.5 11.6893L14.4697 4.71967C14.7626 4.42678 15.2374 4.42678 15.5303 4.71967Z"
                                  fill="#8E2A2F"
                                />
                              </svg>
                            </div>
                            <span className="font-['DM_Sans',sans-serif] text-[#111827] text-[14px] tracking-[-0.1px]">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}

                  <div className="h-[1px] bg-[#f1f2f4] w-full mb-6" />

                  {/* Renewal Info */}
                  <p className="font-['DM_Sans',sans-serif] text-[#687588] text-[14px] tracking-[-0.1px]">
                    {membership.auto_renew && renewsOn
                      ? `Active. Renews on ${renewsOn}`
                      : !membership.auto_renew && renewsOn
                        ? `Active until ${renewsOn}. Will not renew.`
                        : 'Active'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Info Card */}
          <div className="w-full lg:w-[361px] shrink-0 space-y-6">
            <div className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8">
              <div className="w-[48px] h-[48px] rounded-[8px] bg-[#3a3335]/[0.04] flex items-center justify-center mb-6">
                <span className="material-icons text-[#908e8e] text-[22px]">
                  card_membership
                </span>
              </div>
              <h3 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px] mb-3">
                About your membership
              </h3>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] tracking-[-0.1px]">
                Cancelling stops the auto-renewal — you keep your benefits
                until the end of the current period. You can re-subscribe at
                any time.
              </p>
            </div>

            <div className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8">
              <div className="w-[48px] h-[48px] rounded-[8px] bg-[#3a3335]/[0.04] flex items-center justify-center mb-6">
                <span className="material-icons text-[#908e8e] text-[22px]">
                  credit_card
                </span>
              </div>
              <h3 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px] mb-3">
                Payment methods
              </h3>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] tracking-[-0.1px]">
                We don't store your card on Cherry Picked — payment details
                stay with Stripe, our secure payments provider. You can update
                or remove the card you used during your next checkout.
              </p>
            </div>
          </div>
        </div>
      </main>

      <CancelPlanModal
        isOpen={isCancelModalOpen}
        onClose={() => (cancelling ? undefined : setIsCancelModalOpen(false))}
        onConfirm={handleCancelPlan}
        planName={planName}
        expiryDate={renewsOn}
      />

      <Footer />
    </div>
  );
}
